import classNames from "classnames";
import {useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {common as stringsCommon, registerPage as strings} from "strings";

import Button from "components/Button";
import {DateString} from "components/DateInput";
import PasswordRequirements from "components/PasswordRequirements/PasswordRequirements";
import StyledDateInput from "components/StyledDateInput";
import StyledModal from "components/StyledModal";
import TextInput from "components/TextInput";
import UnauthLayout from "components/UnauthLayout";

import {useRegister} from "liberty-core/api/react-query/user";
import {
  formatPrescriptionNumber,
  isValidEmail,
  isValidFirstOrLastName,
} from "liberty-core/sharedFunctions";

import {useAlert} from "lib/alert";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./RegisterPage.module.css";

const errorFieldNames = {
  password: "Password",
  confirmPassword: "ConfirmPassword",
  firstName: "FirstName",
  lastName: "LastName",
  dateOfBirth: "DateOfBirth",
  email: "Email",
  previousScriptNumber: "PreviousScriptNumber",
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const [pharmacy] = useCurrentPharmacy();
  const [searchParams] = useSearchParams();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<DateString>("");
  const [previousScriptNumber, setPreviousScriptNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [apiPrompt, setApiPrompt] = useState("");

  const [fieldErrors, setFieldErrors] = useState<{[field: string]: string[]}>(
    {},
  );

  const {mutateAsync: submitRegisterRequest} = useRegister();
  const [isLoading, setIsLoading] = useState(false);

  const alert = useAlert();

  const setPasswordVal = (newPassword: string) => {
    setPassword(newPassword);
    setFieldErrors((prev) => ({
      ...prev,
      [errorFieldNames.password]: [],
      [errorFieldNames.confirmPassword]: [],
    }));
  };

  const setConfirmPasswordVal = (newConfirmPassword: string) => {
    setConfirmPassword(newConfirmPassword);
    setFieldErrors((prev) => ({
      ...prev,
      [errorFieldNames.password]: [],
      [errorFieldNames.confirmPassword]: [],
    }));
  };

  const register = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (isLoading) {
      return;
    }

    if (password !== confirmPassword) {
      alert({
        title: stringsCommon.whoops,
        message: strings.passwordMatchError,
      });
      setFieldErrors({
        [errorFieldNames.password]: [strings.mustMatch],
        [errorFieldNames.confirmPassword]: [strings.mustMatch],
      });
    } else {
      setIsLoading(true);
      submitRegisterRequest({
        firstName,
        lastName,
        email,
        password,
        dateOfBirth,
        pharmacyId: pharmacy?.id ?? 0,
        previousScriptNumber: parseInt(previousScriptNumber),
        override: false,
      })
        .then((data) => {
          if (data.prompt) {
            setApiPrompt(data.prompt);
          } else {
            navigate(`/verify?${searchParams.toString()}`, {
              state: {
                selections: data.selections,
                temporaryPatientId: data.temporaryPatientId,
              },
            });
          }
        })
        .catch((err) => {
          if (err?.response?.status === 400 && err?.response?.data?.errors) {
            setFieldErrors(err.response.data.errors);
          }

          alert({
            title: stringsCommon.whoops,
            message:
              err?.response?.data?.generic?.join("\n") ??
              strings.genericErrorMessage,
          });
        })
        .finally(() => setIsLoading(false));
    }
  };

  const onSubmitWithPrompt = () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    submitRegisterRequest({
      firstName,
      lastName,
      email,
      password,
      dateOfBirth,
      pharmacyId: pharmacy?.id ?? 0,
      previousScriptNumber: parseInt(previousScriptNumber),
      override: true,
    })
      .then((data) => {
        navigate("/verify", {
          state: {
            selections: data.selections,
            temporaryPatientId: data.temporaryPatientId,
          },
        });
      })
      .catch((err) => {
        alert({
          title: stringsCommon.whoops,
          message:
            err?.response?.data?.generic?.join("\n") ??
            strings.genericErrorMessage,
        });
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <UnauthLayout rightLink={{label: stringsCommon.login, to: "/login"}}>
      <form className={styles.registerInfo}>
        <div className={classNames(styles.registerText, styles.registerInput)}>
          {stringsCommon.register}
        </div>
        <TextInput
          className={styles.registerInput}
          label={stringsCommon.firstName}
          type="text"
          autoComplete="given-name"
          value={firstName}
          onChangeText={(text) => {
            if (isValidFirstOrLastName(text)) {
              setFirstName(text);
              setFieldErrors((prev) => ({
                ...prev,
                [errorFieldNames.firstName]: [],
              }));
            }
          }}
          errorText={fieldErrors[errorFieldNames.firstName]?.join("\n")}
          maxLength={50}
        />
        <TextInput
          className={styles.registerInput}
          label={stringsCommon.lastName}
          type="text"
          autoComplete="family-name"
          value={lastName}
          onChangeText={(text) => {
            if (isValidFirstOrLastName(text)) {
              setLastName(text);
              setFieldErrors((prev) => ({
                ...prev,
                [errorFieldNames.lastName]: [],
              }));
            }
          }}
          errorText={fieldErrors[errorFieldNames.lastName]?.join("\n")}
          maxLength={50}
        />
        <StyledDateInput
          className={styles.registerInput}
          label={stringsCommon.dateOfBirth}
          autoComplete="bday"
          value={dateOfBirth}
          onChangeDate={(date) => {
            setDateOfBirth(date);
            setFieldErrors((prev) => ({
              ...prev,
              [errorFieldNames.dateOfBirth]: [],
            }));
          }}
          errorText={fieldErrors[errorFieldNames.dateOfBirth]?.join("\n")}
        />
        <TextInput
          className={styles.registerInput}
          label={stringsCommon.previousScriptNumber}
          type="text"
          value={previousScriptNumber}
          onChangeText={(text) => {
            setPreviousScriptNumber(formatPrescriptionNumber(text));
            setFieldErrors((prev) => ({
              ...prev,
              [errorFieldNames.previousScriptNumber]: [],
            }));
          }}
          maxLength={9}
          errorText={fieldErrors[errorFieldNames.previousScriptNumber]?.join(
            "\n",
          )}
        />
        <TextInput
          className={styles.registerInput}
          label={stringsCommon.email}
          type="email"
          autoComplete="email"
          value={email}
          onChangeText={(text) => {
            if (isValidEmail(text)) {
              setEmail(text);
              setFieldErrors((prev) => ({
                ...prev,
                [errorFieldNames.email]: [],
              }));
            }
          }}
          errorText={fieldErrors[errorFieldNames.email]?.join("\n")}
          maxLength={50}
        />
        <TextInput
          className={styles.registerInput}
          label={stringsCommon.password}
          type="password"
          autoComplete="new-password"
          value={password}
          onChangeText={setPasswordVal}
          errorText={fieldErrors[errorFieldNames.password]?.join("\n")}
        />
        <TextInput
          className={styles.registerInput}
          label={strings.confirmPassword}
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChangeText={setConfirmPasswordVal}
          errorText={fieldErrors[errorFieldNames.confirmPassword]?.join("\n")}
        />
        <PasswordRequirements password={password} />
        <Button
          className={styles.registerInput}
          variant="filled"
          onClick={register}
          disabled={
            !firstName || !lastName || !email || !password || !confirmPassword
          }
          loading={isLoading}
        >
          {stringsCommon.register}
        </Button>
      </form>
      <StyledModal
        visible={!!apiPrompt}
        onRequestClose={() => {
          if (!isLoading) {
            setApiPrompt("");
          }
        }}
      >
        <div>
          <div className={styles.apiPrompt}>{apiPrompt}</div>
          <Button
            className={styles.apiPromptConfirm}
            variant="filled"
            onClick={onSubmitWithPrompt}
            loading={isLoading}
          >
            {stringsCommon.ok}
          </Button>
          <Button
            variant="unfilled"
            onClick={() => setApiPrompt("")}
            disabled={isLoading}
          >
            {stringsCommon.cancel}
          </Button>
        </div>
      </StyledModal>
    </UnauthLayout>
  );
}
